import React from "react"
import Layout from "../../components/layout"
import Image from "../../components/image"
import Hero from "../../components/hero"
import { PageColumns, Column } from "../../components/page-column"
import ChartMultipleRowsDesktop from "../../images/who-we-are/resources/mutiple-roles-desktop.inline.svg"
import ChartMultipleRowsMobile from "../../images/who-we-are/resources/mutiple-roles-mobile.inline.svg"
import ChartIncomeModelDesktop from "../../images/who-we-are/resources/income-model-desktop.inline.svg"
import ChartIncomeModelMobile from "../../images/who-we-are/resources/income-model-mobile.inline.svg"
import ReadNext from "../../components/read-next"
import Page from "../../components/page"
import ResourcesTable from "../../components/resources-table"
import { Footnotes, FootnoteRef } from "../../components/footnotes"
import { Link, withPrefix } from "gatsby"

export default function Resources() {
  return (
    <Layout>
      <Hero title="Resources" image="who-we-are/resources/hero.jpg" basic />

      <Page>
        <PageColumns>
          <Column classes="text-block">
            <h2>Budget</h2>
            <p>
              In April 2021, the IMF Executive Board authorized a net
              administrative budget for FY 2022 of $1,214 million, along with
              indicative budgets for FY 2023 and FY 2024. Reflecting a
              long-standing tradition of fiscal prudence, this was the 10th year
              in a row that the IMF’s budget remained flat on a real basis, with
              new activities absorbed through continued reprioritization and
              savings. The Board also approved a limit on gross expenditures of
              $1,460 million, which included $210 million in external
              reimbursements for capacity development activities. Additionally,
              the Board approved a temporary increase in the carry-forward of
              unspent FY 2021 resources in the amount of $102 million for
              possible spending in FY 2022, recognizing crisis-related needs.
              Capital funding of $79 million was approved for use over three
              years for building facilities and information technology capital
              projects.
            </p>
            <p>
              The budget for FY 2022 supported the IMF’s efforts to address
              continued crisis needs and address long-term drivers of global
              change, as well as a shift to a hybrid work model and broader
              modernization of the organization. Savings from internal
              reprioritization and the pandemic-related moratorium on travel
              allowed resources to be repurposed toward crisis needs. While
              engagement with authorities remained largely virtual, demand for
              lending shifted from emergency to medium-term operations, with
              some members facing complex debt challenges; financial system
              stability assessments under the FSAP and Article IV consultations
              fully resumed; and the volume of capacity development delivery
              returned to levels closer to those before the pandemic. The
              Executive Board approved the IMF’s strategies in the areas of
              climate change, digital money, and fragile and conflict-affected
              states, as well as a historic SDR allocation of $650 billion, as
              previously noted.
            </p>
            <p>
              Actual administrative expenditures in FY 2022 totaled $1,180
              million, or 97 percent of the approved net budget. Capital
              expenditures in FY 2022 totaled $90 million, including use of
              previously approved funding. Of this, $21 million was for direct
              capital spending on facilities, $60 million for
              information-technology-related expenditures, and $9 million for
              cloud-related licenses.
            </p>
          </Column>
        </PageColumns>

        <ResourcesTable />

        <PageColumns>
          <Column classes="text-block">
            <h2>
              Income model, charges, remuneration, burden sharing, and total
              comprehensive income
            </h2>
            <h3>Income model</h3>
            <p>
              The IMF generates income primarily through its lending and
              investing activities (see Figure 3.1). Lending income is derived
              from the charges levied on the use of credit from the GRA, service
              charges, and commitment fees. In addition, the use of IMF credit
              is subject to surcharges under certain circumstances, as noted in <Link to="/what-we-do/lending">
                 Part 2
              </Link>. The{" "}
              <a href="https://www.elibrary.imf.org/view/IMF071/24764-9781484330876/24764-9781484330876/ch05.xml#ch05lev2sec01">
                IMF’s income model
              </a>{" "}
              also relies on{" "}
              <a href="https://www.elibrary.imf.org/view/IMF071/24764-9781484330876/24764-9781484330876/ch05.xml#ch05lev1sec03">
                investment income
              </a>{" "}
              generated from assets in the Fixed-Income and Endowment
              Subaccounts of the IMF’s Investment Account. Given the public
              nature of the funds, the IMF’s investment policy includes, among
              other things, a careful assessment of acceptable levels of risk,
              as well as safeguards to minimize actual or perceived conflicts of
              interest. In January 2022, the Executive Board approved an{" "}
              <a href="https://www.imf.org/en/Publications/Policy-Papers/Issues/2022/03/16/Rules-And-Regulations-of-The-Investment-Account-515258">
                updated investment strategy
              </a>
              , which includes responsible investing principles related to
              environmental, social, and governance considerations. These will
              be implemented starting in FY 2023.
            </p>

            <figure role="group" className="chart chart--bar">
              <header>
                <div className="chart__intro">
                  <div className="chart__figure-number">Figure 3.1</div>
                  <h3 className="chart__title">Income Model</h3>
                </div>
              </header>

              <div className="util__tablet-up">
                <ChartIncomeModelDesktop />
              </div>

              <div className="util__tablet-down">
                <ChartIncomeModelMobile />
              </div>

              <div className="chart__source" style={{ marginTop: "0.625rem" }}>
                Source: IMF, Finance Department.
                <br />
                Note: Green lines represent elements that were added to the
                income model in 2008.
                <br />
                ¹ As of April 29, 2022, the membership had not adopted the
                dividend policy.
                <br />
              </div>
            </figure>

            <h3>Charges</h3>
            <p>
              Reflecting high levels of lending activity, the IMF’s main source
              of income continues to be{" "}
              <a href="https://www.elibrary.imf.org/view/IMF071/24764-9781484330876/24764-9781484330876/ch05.xml#ch05lev1sec01">
                charges
              </a>{" "}
              levied on outstanding credit. The{" "}
              <a href="https://www.elibrary.imf.org/view/IMF071/24764-9781484330876/24764-9781484330876/ch05.xml#ch05bx01">
                basic rate of charge
              </a>{" "}
              (that is, the interest rate) on IMF financing comprises the SDR
              interest rate plus a fixed margin expressed in basis points, as
              discussed in <Link to="/what-we-do/lending">
                 Part 2
              </Link>. In April 2022, the Executive Board set the
              margin for the rate of charge at 100 basis points for the period
              through April 2024.
            </p>
            <p>
              The IMF also levies{" "}
              <a href="https://www.elibrary.imf.org/view/IMF071/24764-9781484330876/24764-9781484330876/ch05.xml#ch05bx02">
                surcharges
              </a>{" "}
              on large amounts of credit. Surcharges apply to credit outstanding
              that exceeds a defined threshold relative to a member’s quota
              (level-based surcharges), and they are higher when this threshold
              has been exceeded for a defined period of time (time-based
              surcharges) (see <a href={`${withPrefix("/")}downloads/table_2_1_financial_terms_under_imf_general_resources_account_credit.pdf`}>
                  Table 2.1
                </a>).
            </p>
            <p>
              <a href="https://www.elibrary.imf.org/view/IMF071/24764-9781484330876/24764-9781484330876/ch05.xml#ch05lev1sec01">
                In addition to charges and surcharges
              </a>
              , the IMF levies service charges, commitment fees, and special
              charges. A service charge of 0.5 percent is levied on each drawing
              from the GRA. A refundable{" "}
              <a href="https://www.elibrary.imf.org/view/IMF071/24764-9781484330876/24764-9781484330876/ch05.xml#ch05bx03">
                commitment fee
              </a>{" "}
              is charged at the beginning of each 12-month period on amounts
              available for drawing under GRA arrangements (except for the
              Short-Term Liquidity Line) during that period. The IMF also levies{" "}
              <a href="https://www.elibrary.imf.org/view/IMF071/24764-9781484330876/24764-9781484330876/ch06.xml#ch06lev2sec07">
                special charges
              </a>{" "}
              on charges that are past due, for the first six months a member is
              in arrears.
            </p>
            <h3>Remuneration and interest on borrowing</h3>
            <p>
              On the expenditure side, the IMF{" "}
              <a href="https://www.elibrary.imf.org/view/IMF071/24764-9781484330876/24764-9781484330876/ch02.xml?redirect=true#ch02lev2sec14">
                pays interest (remuneration)
              </a>{" "}
              to members on their creditor positions in the GRA (known as
              “remunerated reserve tranche positions”). The basic rate of
              remuneration is equal to the SDR interest rate. The IMF also pays
              interest at the SDR interest rate on outstanding borrowing under
              the New Arrangements to Borrow (NAB; see the “IMF Financing”
              section).
            </p>
            <h3>Burden sharing</h3>
            <p>
              The rates of charge and remuneration are adjusted under a{" "}
              <a href="https://www.elibrary.imf.org/view/IMF071/24764-9781484330876/24764-9781484330876/ch06.xml#ch06bx03">
                burden-sharing mechanism
              </a>{" "}
              that distributes the cost of overdue financial obligations equally
              between debtor and creditor members.
            </p>
            <h3>Total comprehensive income</h3>
            <p>
              The IMF’s total comprehensive income in FY 2022 was SDR 3.085
              billion ($4.147 billion), reflecting primarily income from the
              high levels of lending activity, endowment income, and gains
              stemming from the remeasurement of the assets and liabilities of
              the IMF’s employee benefit plans, in accordance with International
              Financial Reporting Standards (International Accounting Standard
              19, Employee Benefits).
            </p>
            <h3>Arrears to the IMF</h3>
            <p>
              Sudan, the last member country with arrears, cleared its
              protracted arrears (outstanding for more than six months) to the
              IMF on June 29, 2021, and reached its HIPC Initiative decision
              point. At the end of April 2022, the IMF had no remaining arrears
              cases. The IMF has in place a strengthened cooperative strategy on
              arrears, based on a set of increasingly severe remedial measures
              to prevent the resurgence of protracted arrears.
            </p>
          </Column>
        </PageColumns>

        <PageColumns modifiers="page-columns--spacing-medium" media>
          <Column location="right">
            <Image
              path="who-we-are/resources/resources-5.jpg"
              alt="A male and two female colleagues on a sidewalk in Washington, D.C."
              caption="United States"
            />
          </Column>
        </PageColumns>

        <PageColumns>
          <Column classes="text-block">
            <h2>Financing</h2>
            <p>
              The IMF provides financing to its members through three channels,
              all of which serve the common purpose of transferring reserve
              currencies to member countries: regular (nonconcessional) lending
              from the GRA and concessional lending from the PRGT (both
              discussed in <Link to="/what-we-do/lending">
                 Part 2
              </Link>), as well as the SDR Department. The most
              salient feature of the IMF’s financial structure is that it is
              continuously evolving. The IMF has introduced and refined a
              variety of lending facilities and policies over the years to
              address changing conditions in the global economy or the specific
              needs and circumstances of its members. The RST will be an
              additional channel for lending once lending starts (which is
              expected to take place later in 2022).
            </p>

            <h3>Quotas: Where the IMF gets its money</h3>
            <p>
              The IMF’s 190 member countries provide resources for loans
              primarily through their payment of{" "}
              <a href="https://www.imf.org/en/About/Factsheets/Sheets/2016/07/14/12/21/IMF-Quotas">
                quotas
              </a>
              , which also set their voting rights. Multilateral borrowing and
              bilateral borrowing serve as second and third lines of defense in
              times of crisis. These resources give the IMF access to about $1
              trillion in nonconcessional lending firepower to support members.
              Concessional lending and debt relief for low-income countries are
              financed through separate contribution-based trust funds.
            </p>

            <div className="util__tablet-up" style={{ margin: "1.25rem 0" }}>
              <ChartMultipleRowsDesktop style={{ margin: "auto" }} />
            </div>

            <div className="util__tablet-down" style={{ margin: "1.25rem 0" }}>
              <ChartMultipleRowsMobile style={{ margin: "auto" }} />
            </div>

            <p>
              Each member is assigned a quota based broadly on its position in
              the world economy. IMF quotas total SDR 476 billion (about $639.9
              billion).
              <FootnoteRef id={1} /> The value of the SDR, the IMF’s unit of
              account, is based on a basket of currencies (see the “Special
              Drawing Right” section).
            </p>
            <p>
              IMF quotas are also reviewed regularly. The 16th General Review of
              Quotas, which is currently underway and is expected to be
              completed no later than December 15, 2023, offers an opportunity
              to assess the overall adequacy of quotas as well as the adequacy
              of their distribution among IMF member countries. The Executive
              Board issued two progress reports to the Board of Governors on the
              16th General Review during FY 2022, reporting on ongoing
              discussions. The review is building on the governance reforms of
              the 2010 review (14th General Review of Quotas), including efforts
              to protect quotas and voting shares of the poorest members. The
              current formula for determining quotas, which was approved in 2008
              and has been used as a guide, is also under review.
            </p>
            <h3>Quota payments</h3>
            <p>
              The conditions for implementing the quota increases approved under
              the 14th General Review were met on January 26, 2016. This
              resulted in a doubling of quota resources, to SDR 477 billion
              (about $641.2 billion) from about SDR 238.5 billion (about $320.6
              billion). As of April 30, 2022, all but 2 of the 190 members had
              made their quota payments, accounting for more than 99 percent of
              the total quota increases, and total quotas stood at SDR 476
              billion (about $639.9 billion).
            </p>
          </Column>
        </PageColumns>

        <PageColumns modifiers="page-columns--spacing-medium" media>
          <Column location="left">
            <Image
              path="who-we-are/resources/resources-6.jpg"
              alt="A woman in an orange-red hijab at a conference"
              caption="United States"
            />
          </Column>
        </PageColumns>

        <PageColumns>
          <Column classes="text-block">
            <h3>Borrowing by the IMF </h3>
            <p>
              As noted, the IMF is a quota-based institution. However, borrowed
              resources continue to play a key role in supplementing quota
              resources through the NAB and the bilateral borrowing agreements
              (BBAs), serving respectively as a second and third line of defense
              after quotas.
            </p>
            <p>
              The NAB are a set of credit arrangements with 38 participants and
              2 prospective participants. The size of the NAB was doubled to
              about SDR 361 billion on January 1, 2021, and a new NAB period was
              set through the end of 2025. NAB resources can be activated when
              the IMF’s resources need to be supplemented to forestall or cope
              with an impairment of the international monetary system.
              Activation requires the consent of participants representing 85{" "}
              percent of total credit arrangements of participants eligible to
              vote, as well as the approval of the Executive Board. The NAB were
              activated 10 times between April 2011 and February 2016, the most
              recent activation.
            </p>
            <p>
              As noted, BBAs are intended to serve as a third line of defense
              after quotas and the NAB. The current (2020) round of BBAs has
              been in effect since January 1, 2021, with an initial term through
              December 31, 2023, which may be extended by one more year. As of
              April 30, 2022, 42 bilateral creditors had committed under their
              2020 BBAs to provide the IMF with a total credit amount equivalent
              to about SDR 138 billion. Resources under BBAs can be activated
              only if the amount of the IMF resources otherwise available for
              financing has fallen below a threshold of SDR 100 billion and
              either the NAB has been activated or there are no available
              uncommitted NAB resources. Activation of BBAs requires approval by
              bilateral creditors representing 85 percent of the total credit
              amount committed.
            </p>

            <h3>Special Drawing Right</h3>
            <p>
              The{" "}
              <a href="https://www.imf.org/en/Topics/special-drawing-right/">
                SDR
              </a>{" "}
              is an international reserve asset the IMF created in 1969 to
              supplement its member countries’ official reserves. It serves as
              the unit of account of the IMF and some other international
              organizations. The SDR is neither a currency nor a claim on the
              IMF. Rather, it is a potential claim on the freely usable
              currencies of IMF members. IMF members who are participants in the
              SDR Department (currently all members) may exchange SDRs for
              freely usable currencies.
            </p>
            <p>
              The SDR’s value is currently based on a basket of five currencies:
              the US dollar, the euro, the Chinese renminbi, the Japanese yen,
              and the British pound. The currencies included are reviewed
              periodically; the{" "}
              <a href="https://www.imf.org/en/News/Articles/2022/07/29/pr22281-press-release-imf-determines-new-currency-amounts-for-the-sdr-valuation-basket">
                most recent review of the valuation of the SDR basket
              </a>{" "}
              occurred in July 2022.
            </p>
            <p>
              As of April 30, 2022, a total of SDR 660.7 billion (equivalent to
              about $888 billion) had been allocated to members, including the
              August 2021 allocation of SDR 456.5 billion, the largest
              allocation of SDRs in history, in the context of the ongoing
              pandemic. This SDR allocation provided additional liquidity to the
              global economic system—supplementing countries’ foreign exchange
              reserves and reducing their reliance on more expensive domestic or
              external debt. Countries were able to use the space provided by
              the SDR allocation to support their economies and step up the
              fight against the crisis.
            </p>
            <p>
              To amplify the benefits of this allocation, the IMF is encouraging
              voluntary channeling of SDRs from countries with strong external
              positions to countries most in need. Some members have already
              pledged to lend their SDRs to the PRGT, which provides
              concessional loans to low-income countries. Furthermore, the{" "}
              <a href="https://www.imf.org/en/News/Articles/2022/04/13/pr22115-imf-md-welcomes-the-creation-of-the-rst-to-help-vulnerable-countries">
                recently established RST
              </a>{" "}
              will use channeled SDRs to provide affordable longer-term
              financing to support countries undertaking reforms to reduce
              risks, including those related to climate change, and for pandemic
              preparedness.
            </p>
            <p>
              In light of the new allocation and to enhance transparency and
              accountability in the use of SDRs, the IMF issued a{" "}
              <a href="https://www.imf.org/en/Publications/Policy-Papers/Issues/2021/08/19/Guidance-Note-for-Fund-Staff-on-the-Treatment-and-Use-of-SDR-Allocations-464319">
                Staff Guidance Note
              </a>{" "}
              in August 2021 on assessing the macroeconomic implications of the
              new allocation, its statistical treatment and governance, and how
              it might affect debt sustainability. In addition, the IMF started
              reporting quarterly on SDR holdings, transactions, and trading and
              committed to issuing a follow-up report on the use of SDRs in two
              years.
            </p>
          </Column>
        </PageColumns>

        <PageColumns>
          <Column>
            <Footnotes
              footnotes={[
                "Two member countries, Eritrea and Syria, have not yet consented to their proposed quota increases under the 14th General Review of Quotas. Once these countries consent to, and pay for, their respective quota increases, IMF quotas will total SDR 477 billion.",
              ]}
            />
          </Column>
        </PageColumns>
      </Page>

      <ReadNext sectionID="who-we-are" pageID="accountability" />
    </Layout>
  )
}
